import _ from 'lodash';
import Immutable from 'immutable';

import orderedMapFromEntityImmutables from '../../immutable_converters/lib/ordered_map_from_entity_immutables';
import ErrImmutableConverter from '../../immutable_converters/err_converter';

export default function createCollectionStoreClass({ converter }) {
  class CollectionStore {
    constructor(binding, { createProvider } = {}) {
      this.immutableStore = new ImmutableStore(binding, this._isMatch.bind(this));
      if (!createProvider) {
        createProvider = CollectionProvider.create;
      }
      this.provider = createProvider(this.immutableStore, { fromImmutable: this._fromImmutable.bind(this) });
    }

    get binding() {
      return this.immutableStore.binding;
    }

    getProvider() {
      return this.provider;
    }

    set(entities) {
      let newMap = this.orderedMapFromEntities(entities);
      this.immutableStore.set(newMap);
      this.getProvider().clearCache();
    }

    // converts an array of `entities` into an `OrderedMap` with the same
    // order as the passed in `entities` and indexed by `id`
    orderedMapFromEntities(entities) {
      let immutables = entities.map(converter.toImmutable);
      return this.orderedMapFromEntityImmutables(immutables);
    }

    // returns an OrderedMap indexed by `id` and sorted in the same order as
    // the passed in `entityImmutables`
    orderedMapFromEntityImmutables(entityImmutables) {
      return orderedMapFromEntityImmutables(entityImmutables);
    }

    add(entity) {
      let found = !this.immutableStore.findAll({ id: entity.id }).isEmpty();
      if (found) {
        throw new Error(`entity with id [${entity.id}] already exists`);
      }
      this.addOrReplace(entity);
    }

    addOrReplace(entity) {
      this.immutableStore.put(converter.toImmutable(entity));
    }

    replace(updatedEntity) {
      this.immutableStore.find(updatedEntity.id);
      this.addOrReplace(updatedEntity);
    }

    find(entityId) {
      return this._fromImmutable(this.immutableStore.find(entityId));
    }

    findAll(query) {
      return this.immutableStore
        .findAll(query)
        .map(imm => {
          if (query && query.select) {
            if (!_.isArray(query.select)) {
              throw new Error(`expected an array for select option, got[${query.select}]`);
            }
            return imm.filter((v, k) => query.select.includes(k)).toJS();
          }
          return this._fromImmutable(imm);
        })
        .toArray();
    }

    findBy(query) {
      return this.findAll(query)[0];
    }

    has(query) {
      return this.immutableStore.has(query);
    }

    setPending(entity) {
      this.immutableStore.setPending(converter.toImmutable(entity));
    }

    getPending(entityId) {
      let immutable = this.immutableStore.getPending(entityId);
      return immutable && this._fromImmutable(immutable);
    }

    isPending(entityId) {
      return !!this.immutableStore.getPending(entityId);
    }

    setPendingNew(entity) {
      this.immutableStore.setPendingNew(converter.toImmutable(entity));
    }

    getPendingNew() {
      let immutable = this.immutableStore.getPendingNew();
      return immutable && this._fromImmutable(immutable);
    }

    isPendingNew() {
      return !!this.immutableStore.getPendingNew();
    }

    _fromImmutable(immutableEntity) {
      return converter.fromImmutable(immutableEntity);
    }

    _isMatch(plainObject, query) {
      return _.isMatch(plainObject, query);
    }
  }

  // Delegate the rest of CollectionStore APIs to ImmutableStore
  [
    'count',
    'remove',
    'clearErrors',
    'clearErrorsForNew',
    'getAllErrors',
    'getErrors',
    'getErrorsForNew',
    'setErrors',
    'setErrorsForNew',
    'clearErrorForLoading',
    'getErrorForLoading',
    'setErrorForLoading',
    'commitPending',
    'commitPendingNew',
    'resetPending',
    'resetPendingNew',
    'isPendingDelete',
    'setPendingDelete',
    'isLoading',
    'resetLoading',
    'setLoading',
  ].forEach(m => {
    CollectionStore.prototype[m] = function(...args) {
      return this.immutableStore[m](...args);
    };
  });

  return CollectionStore;
}

class ImmutableStore {
  constructor(binding, isMatch) {
    this.binding = binding;
    if (!this.binding.get()) {
      this.binding.set(Immutable.OrderedMap());
    }
    this._isMatch = isMatch;
  }

  /////////////////////
  // Immutable Entity
  /////////////////////
  count() {
    let entityMap = this.binding.get();
    return entityMap ? entityMap.count() : 0;
  }

  find(entityId) {
    if (!entityId) {
      throw new Error(`entity id cannot be [${entityId}]`);
    }

    let immutable = this._findById(entityId);
    if (!immutable) {
      throw new Error(`could not find entity with id [${entityId}]`);
    }

    return immutable;
  }

  findAll(query) {
    let entries = this.binding.get();
    if (!entries) {
      return Immutable.OrderedMap();
    }

    let filter = query && (query.filter || query.select || query.sortBy) ? query.filter : query; // backward compat
    if (filter) {
      if (!_.isPlainObject(filter) && !_.isFunction(filter)) {
        throw new Error(`expected an object or function for filter option, got [${filter}]`);
      }
      let filterAttrs = _.keys(filter);
      if (filterAttrs.length === 1 && filterAttrs[0] === 'id') {
        let foundImm = this._findById(filter.id);

        return Immutable.OrderedMap(foundImm ? [[filter.id, foundImm]] : []);
      }

      entries = entries.filter(imm => (_.isFunction(filter) ? filter(imm.toJS()) : this._isMatch(imm.toJS(), filter)));
    }

    if (query && query.sortBy) {
      if (!_.isFunction(query.sortBy)) {
        throw new Error(`expected a function for sortBy option, got [${query.sortBy}]`);
      }
      entries = entries.sortBy(imm => query.sortBy(imm.toJS()));
    }

    return entries;
  }

  _findById(entityId) {
    if (!entityId) {
      return undefined;
    }
    return this.binding.get(entityId);
  }

  has(query) {
    if (query.id && _.keys(query).length === 1) {
      const imm = this.binding.get();
      return !!imm && imm.has(query.id);
    }
    throw new Error('not yet implemented. can only query "has" by id.');
  }

  put(immutable) {
    if (_(immutable.get('id')).isEmpty()) {
      throw new Error('id cannot be blank');
    }

    this.binding.update(immutable.get('id'), currentImm => {
      let newImm = immutable.merge(
        (currentImm || Immutable.Map()).filter((v, k) => ['_errors', '_pending'].indexOf(k) !== -1)
      );
      return Immutable.is(newImm, currentImm) ? currentImm : newImm;
    });
  }

  remove(entityId) {
    this.binding.remove(entityId);
  }

  set(orderedMap) {
    this.binding.update(currentMap => (Immutable.is(orderedMap, currentMap) ? currentMap : orderedMap));
    this.binding.meta('newEntity').remove();
  }

  /////////////////////
  // Pending Entity
  /////////////////////
  commitPending(entityId) {
    if (this.binding.get([entityId, '_pending'])) {
      this.binding.set(entityId, this.binding.get([entityId, '_pending']));
      this.resetPending(entityId);
      return;
    }

    if (this.binding.get([entityId, '_pendingDelete'])) {
      this.remove(entityId);
    }
  }

  getPending(entityId) {
    this.find(entityId);
    return this.binding.get([entityId, '_pending']);
  }

  resetPending(entityId) {
    this.binding.remove([entityId, '_pending']);
    this.binding.remove([entityId, '_pendingDelete']);
  }

  setPending(immutable) {
    let entityId = immutable.get('id');
    this.find(entityId);
    this.clearErrors(entityId);
    this.binding.set([entityId, '_pending'], immutable);
  }

  setPendingDelete(entityId) {
    if (this.getPending(entityId)) {
      throw new Error(`cannot set pending delete while update is pending for entity [${entityId}]`);
    }
    this.clearErrors(entityId);
    this.binding.set([entityId, '_pendingDelete'], true);
  }

  isPendingDelete(entityId) {
    this.find(entityId);
    return this.binding.get([entityId, '_pendingDelete']) || false;
  }

  setPendingNew(immutable) {
    let pendingNewEntity = this._pendingNewEntityBinding.get();
    if (pendingNewEntity) {
      if (pendingNewEntity.equals(immutable)) {
        return;
      }
      throw new Error(`cannot overwrite pending new entity [${pendingNewEntity.get('id')}]`);
    }

    this.clearErrorsForNew();
    this._pendingNewEntityBinding.set(immutable);
  }

  getPendingNew() {
    return this._pendingNewEntityBinding.get();
  }

  commitPendingNew() {
    let pendingNewEntity = this._pendingNewEntityBinding.get();
    if (pendingNewEntity) {
      this.binding.set(pendingNewEntity.get('id'), pendingNewEntity);
      this.resetPendingNew();
    }
  }

  resetPendingNew() {
    this._pendingNewEntityBinding.remove();
  }

  isLoading() {
    return !!this._loadingBinding.get();
  }

  resetLoading() {
    this._loadingBinding.remove();
  }

  setLoading() {
    this.clearErrors();
    this._loadingBinding.set(true);
  }

  get _loadingBinding() {
    return this.binding.meta('_loading');
  }

  /////////////////////
  // Errors
  /////////////////////
  clearErrors(entityId) {
    this.binding.remove([entityId, '_errors']);
  }

  clearErrorsForNew() {
    this._newEntityErrorBinding.remove();
  }

  getErrorsForNew() {
    return errorsFromImm(this._newEntityErrorBinding.get());
  }

  getAllErrors() {
    let entries = this.binding.get() || Immutable.OrderedMap();
    entries = entries
      .filter(immutable => immutable.has('_errors'))
      .reduce((acc, imm) => {
        acc[imm.get('id')] = errorsFromImm(imm.get('_errors'));
        return acc;
      }, {});
    return entries;
  }

  getErrors(entityId) {
    this.find(entityId);
    return errorsFromImm(this.binding.get([entityId, '_errors']));
  }

  setErrors(entityId, errors) {
    this.find(entityId);
    this.binding.set([entityId, '_errors'], errorsAsImm(errors));
  }

  setErrorsForNew(errors) {
    this._newEntityErrorBinding.set(errorsAsImm(errors));
  }

  getErrorForLoading() {
    return (
      this._collectionLoadingErrorBinding.get() &&
      ErrImmutableConverter.fromImmutable(this._collectionLoadingErrorBinding.get())
    );
  }

  setErrorForLoading(error) {
    this._collectionLoadingErrorBinding.set(ErrImmutableConverter.toImmutable(error));
  }

  clearErrorForLoading() {
    this._collectionLoadingErrorBinding.remove();
  }

  get _collectionLoadingErrorBinding() {
    return this.binding.meta('loading.error');
  }

  get _newEntityErrorBinding() {
    return this.binding.meta('newEntity.errors');
  }

  get _pendingNewEntityBinding() {
    return this.binding.meta('newEntity.pending');
  }
}

export class CollectionProvider {
  constructor(immutableStore, converter) {
    this.immutableStore = immutableStore;
    this.converter = converter;
    this._entityCache = {};
  }

  get bindings() {
    return [this.immutableStore.binding];
  }

  count() {
    return this.immutableStore.count();
  }

  clearCache() {
    this._entityCache = {};
  }

  find(entityId) {
    return this._fromImmutableWithCache(entityId, this.immutableStore.find(entityId));
  }

  findAll(query) {
    if (query && query.select && !_.isArray(query.select)) {
      throw new Error(`expected an array for select option, got [${query.select}]`);
    }

    return this.immutableStore
      .findAll(query)
      .map((imm, entityId) => {
        if (query && query.select) {
          return imm.filter((v, k) => query.select.includes(k)).toJS();
        }
        return this._fromImmutableWithCache(entityId, imm);
      })
      .toArray();
  }

  findBy(query) {
    let found = this.findAll(query)[0];
    if (!found && _.keys(query).length === 1 && query.id) {
      this._removeFromCache(query.id);
    }

    return found;
  }

  _fromImmutableWithCache(entityId, immutable) {
    let cacheEntry = this._entityCache[entityId];
    if (cacheEntry && cacheEntry.immutable === immutable) {
      return cacheEntry.entity;
    }

    let entity = this.converter.fromImmutable(immutable);
    this._entityCache[entityId] = { entity, immutable };
    return entity;
  }

  _removeFromCache(entityId) {
    delete this._entityCache[entityId];
  }

  getAllErrors() {
    return this.immutableStore.getAllErrors();
  }

  getErrors(entityId) {
    return this.immutableStore.getErrors(entityId);
  }

  getErrorsForNew() {
    return this.immutableStore.getErrorsForNew();
  }

  getErrorForLoading() {
    return this.immutableStore.getErrorForLoading();
  }

  getPending(entityId) {
    return this.converter.fromImmutable(this.immutableStore.getPending(entityId));
  }

  getPendingNew() {
    let immutable = this.immutableStore.getPendingNew();
    return immutable && this.converter.fromImmutable(immutable);
  }

  isPending(entityId) {
    return !!this.immutableStore.getPending(entityId);
  }

  isPendingDelete(entityId) {
    return this.immutableStore.isPendingDelete(entityId);
  }

  isPendingNew() {
    return !!this.immutableStore.getPendingNew();
  }

  isLoading() {
    return this.immutableStore.isLoading();
  }

  static create(immutableStore, options) {
    return new CollectionProvider(immutableStore, options);
  }
}

function errorsAsImm(errors) {
  return Immutable.List(errors.map(ErrImmutableConverter.toImmutable));
}

function errorsFromImm(errorList) {
  return errorList ? errorList.toArray().map(ErrImmutableConverter.fromImmutable) : [];
}



// WEBPACK FOOTER //
// ./src/scripts/adapters/stores/lib/create_collection_store_class.js